import React from "react";
import PropTypes from "prop-types";
import { alpha, styled } from "@material-ui/core";
import { formatEstadoPropuesta } from "src/utils/propuestasV2/formatEstadoPropuesta";

// Mapeo de estados a colores semánticos
const STATUS_COLORS = {
  // V1
  CREADA: "info",
  ENVIADA: "warning",
  ACEPTADO_CLIENTE: "primary",
  ACEPTADA: "success",
  RECHAZADA: "error",
  CADUCADA: "default",
  MIGRADA: "default",
  // V2
  BORRADOR: "info",
  PENDIENTE: "warning",
  PARCIALMENTE_ACEPTADA: "warning",
  EN_RENEGOCIACION: "info",
};

const STATUS_ICONS = {
  // V1
  CREADA: "📝",
  ENVIADA: "📤",
  ACEPTADO_CLIENTE: "👍",
  ACEPTADA: "✅",
  RECHAZADA: "❌",
  CADUCADA: "⌛",
  MIGRADA: "🔄",
  // V2
  BORRADOR: "📝",
  PENDIENTE: "📤",
  PARCIALMENTE_ACEPTADA: "⚠️",
  EN_RENEGOCIACION: "💬",
};

const RootStyle = styled("span")(({ theme, styleProps }) => {
  const { color, variant } = styleProps;

  const styleFilled = (color) => ({
    color: theme.palette[color].contrastText,
    backgroundColor: theme.palette[color].main,
  });

  const styleOutlined = (color) => ({
    color: theme.palette[color].main,
    backgroundColor: "transparent",
    border: `1px solid ${theme.palette[color].main}`,
  });

  const styleGhost = (color) => ({
    color: theme.palette[color].dark,
    backgroundColor: alpha(theme.palette[color].main, 0.16),
  });

  return {
    height: 24,
    minWidth: 24,
    lineHeight: 0,
    borderRadius: 12,
    cursor: "default",
    alignItems: "center",
    whiteSpace: "nowrap",
    display: "inline-flex",
    justifyContent: "center",
    padding: theme.spacing(0.5, 1.5),
    color: theme.palette.grey[800],
    fontSize: theme.typography.pxToRem(13),
    fontFamily: theme.typography.fontFamily,
    backgroundColor: theme.palette.grey[300],
    fontWeight: theme.typography.fontWeightBold,
    transition: theme.transitions.create(
      ["background-color", "color", "box-shadow"],
      {
        duration: theme.transitions.duration.shorter,
      },
    ),

    ...(color !== "default"
      ? {
          ...(variant === "filled" && { ...styleFilled(color) }),
          ...(variant === "outlined" && { ...styleOutlined(color) }),
          ...(variant === "ghost" && { ...styleGhost(color) }),
        }
      : {
          ...(variant === "outlined" && {
            backgroundColor: "transparent",
            color: theme.palette.text.primary,
            border: `1px solid ${theme.palette.grey[500_32]}`,
          }),
          ...(variant === "ghost" && {
            color: theme.palette.text.secondary,
            backgroundColor: theme.palette.grey[500_16],
          }),
        }),
  };
});

const ProposalStatusLabel = ({
  estado,
  variant = "ghost",
  showIcon = true,
  ...other
}) => {
  const color = STATUS_COLORS[estado] || "default";
  const icon = showIcon ? STATUS_ICONS[estado] : "";

  return (
    <RootStyle styleProps={{ color, variant }} {...other}>
      {icon && <span style={{ marginRight: "4px" }}>{icon}</span>}
      {formatEstadoPropuesta(estado)}
    </RootStyle>
  );
};

ProposalStatusLabel.propTypes = {
  estado: PropTypes.oneOf([
    "CREADA",
    "ENVIADA",
    "ACEPTADO_CLIENTE",
    "ACEPTADA",
    "RECHAZADA",
    "CADUCADA",
    "MIGRADA",
    "BORRADOR",
    "PENDIENTE",
    "PARCIALMENTE_ACEPTADA",
    "EN_RENEGOCIACION",
  ]).isRequired,
  variant: PropTypes.oneOf(["filled", "outlined", "ghost"]),
  showIcon: PropTypes.bool,
};

export default ProposalStatusLabel;
